import { useAuthStore } from '@greensm/auth';
import type { ServerEvent } from '@greensm/api-client';
import { useEventStream, type SseMessage } from '@greensm/sse';
import { BottomSheet, Icon, useToast, type IconName } from '@greensm/ui';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useNotificationFeed } from '@/features/notifications/hooks';
import widgetJelly from '@/assets/brand/widget-floating-4jelly.png';
import { useServices } from '../services';
import { V3StreamProvider } from '../v3Stream';

const TABS: { to: string; icon: IconName; label: string; end?: boolean }[] = [
  { to: '/', icon: 'home', label: 'Trang chủ', end: true },
  { to: '/history', icon: 'clock', label: 'Hoạt động', end: true },
  { to: '/notifications', icon: 'bell', label: 'Thông báo' },
  { to: '/account', icon: 'user', label: 'Tài khoản' },
];
const NAV_PATHS = new Set(['/', '/history', '/notifications', '/account']);

const LAUNCHER = [
  { to: '/book', label: 'Đặt xe' },
  { to: '/support', label: 'Trung tâm hỗ trợ' },
  { to: '/history', label: 'Lịch sử chuyến' },
  { to: '/notifications', label: 'Thông báo' },
];

/** Khung cho khu vực đã đăng nhập: điều hướng nổi, đồng bộ thời gian thực (SSE), cảnh báo mất kết nối. */
export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const auth = useAuthStore();
  const { eventsUrl, getAccessToken, fetchImpl, v3Enabled } = useServices();
  const [launcher, setLauncher] = useState(false);
  const feed = useNotificationFeed('all');

  const status = useEventStream({
    url: eventsUrl,
    getToken: getAccessToken,
    fetchImpl,
    onUnauthorized: () => auth.refresh(),
    onMessage: (m: SseMessage) => {
      let ev: ServerEvent | null = null;
      try { ev = JSON.parse(m.data) as ServerEvent; } catch { return; }
      const kind = (ev as ServerEvent & { event_type?: string }).event_type ?? ev.type;
      switch (kind) {
        case 'trip.changed':
        case 'trip.updated': void qc.invalidateQueries({ queryKey: ['trip'] }); void qc.invalidateQueries({ queryKey: ['trips'] }); break;
        case 'proposal.changed':
        case 'consent.recorded':
        case 'plan.updated': void qc.invalidateQueries({ queryKey: ['plan'] }); void qc.invalidateQueries({ queryKey: ['trip'] }); break;
        case 'command.changed':
        case 'command.updated': void qc.invalidateQueries({ queryKey: ['command'] }); break;
        case 'notification.changed':
        case 'notification.created': void qc.invalidateQueries({ queryKey: ['notifications'] }); toast('Bạn có thông báo mới'); break;
        case 'support.changed': void qc.invalidateQueries({ queryKey: ['support'] }); break;
        case 'stream.reset': void qc.invalidateQueries(); break;
      }
    },
    // Sau khi nối lại: làm mới mọi dữ liệu đang xem để bù các sự kiện có thể đã lỡ.
    onReconnected: () => void qc.invalidateQueries(),
  });

  const showNav = NAV_PATHS.has(pathname);

  return (
    <>
      {status === 'reconnecting' && (
        <div className="g-banner g-banner--warn app-offline" role="status">Mất kết nối cập nhật trực tiếp. Đang thử kết nối lại…</div>
      )}
      <main className={`app-main${showNav ? '' : ' app-main--nonav'}${pathname.startsWith('/book') ? ' app-main--booking' : ''}`} id="main" tabIndex={-1}>
        {/* Luồng SSE v3 (/api/v3/stream) chỉ mở khi có Backend v3 (live) hoặc mock v3 – màn cũ không bị ảnh hưởng. */}
        <V3StreamProvider enabled={v3Enabled}>
          <Outlet />
        </V3StreamProvider>
      </main>

      {showNav && (
        <nav className="g-nav" aria-label="Điều hướng chính">
          <div className="g-nav__pill">
            {TABS.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} className="g-nav__item" aria-label={t.label}>
                <Icon name={t.icon} />
                {t.to === '/notifications' && feed.unread && <span className="g-nav__dot" aria-label="Có thông báo chưa đọc" />}
              </NavLink>
            ))}
          </div>
          <button type="button" className="g-nav__launch" aria-label="Tất cả dịch vụ" onClick={() => setLauncher(true)}>
            <img src={widgetJelly} alt="" aria-hidden="true" width={38} height={38} style={{ objectFit: 'contain', display: 'block' }} />
          </button>
        </nav>
      )}

      <BottomSheet open={launcher} onClose={() => setLauncher(false)} label="Tất cả dịch vụ">
        <h2 className="g-h2" style={{ marginBottom: 12 }}>Tất cả dịch vụ</h2>
        {LAUNCHER.map((l) => (
          <button key={l.to} type="button" className="g-card g-card--btn g-row g-between" style={{ marginBottom: 8, padding: '14px 16px' }}
            onClick={() => { setLauncher(false); navigate(l.to); }}>
            <b>{l.label}</b><Icon name="chevron" size={20} />
          </button>
        ))}
      </BottomSheet>
    </>
  );
}
